import {
  ALLERGENS,
  ALLERGEN_LABELS,
  type Allergen,
  type IngredientCategory,
  type IngredientDto,
} from '@fewerbags/schema';

/** Pure display helpers for the shared ingredient catalogue — no I/O, easy to unit test. */

export const CATEGORY_LABEL: Record<IngredientCategory, string> = {
  produce: 'Produce',
  meat: 'Meat',
  seafood: 'Seafood',
  dairy: 'Dairy',
  bakery: 'Bakery',
  pantry: 'Pantry',
  frozen: 'Frozen',
  spices: 'Spices',
  beverages: 'Beverages',
  other: 'Other',
};

export function categoryLabel(category: IngredientCategory | null | undefined): string {
  return category ? CATEGORY_LABEL[category] : 'Uncategorized';
}

/** "3 days" / "1 day" / "Unknown" — used for the per-location shelf-life fields. */
export function shelfLifeLabel(days: number | null | undefined): string {
  if (days === null || days === undefined) return 'Unknown';
  if (days === 0) return 'Same day';
  if (days === 1) return '1 day';
  return `${days} days`;
}

export interface AllergenRow {
  allergen: Allergen;
  label: string;
  /** What everyone sees (classifier + two-household agreement). */
  shared: boolean;
  /** What this household's planner uses. */
  effective: boolean;
  /** This household has its own vote on this allergen. */
  corrected: boolean;
}

/** Every allergen with its shared / household state, in `ALLERGENS` order. */
export function allergenRows(
  dto: Pick<IngredientDto, 'allergens' | 'householdAllergens' | 'allergenCorrections'>,
): AllergenRow[] {
  const shared = new Set<string>(dto.allergens);
  const effective = new Set<string>(dto.householdAllergens);
  const corrected = new Set<string>(dto.allergenCorrections.map((c) => c.allergen));
  return ALLERGENS.map((allergen) => ({
    allergen,
    label: ALLERGEN_LABELS[allergen],
    shared: shared.has(allergen),
    effective: effective.has(allergen),
    corrected: corrected.has(allergen),
  }));
}

/**
 * The vote a tap sends: flip this household's view, withdrawing the vote
 * when the flip lands back on the shared value.
 */
export function toggleAllergenVote(row: AllergenRow): {
  allergen: Allergen;
  present: boolean | null;
} {
  const next = !row.effective;
  return { allergen: row.allergen, present: next === row.shared ? null : next };
}

/** Apply a vote to a cached DTO (optimistic update). */
export function withAllergenVote<
  T extends Pick<IngredientDto, 'allergens' | 'householdAllergens' | 'allergenCorrections'>,
>(dto: T, vote: { allergen: Allergen; present: boolean | null }): T {
  const corrections = dto.allergenCorrections.filter((c) => c.allergen !== vote.allergen);
  if (vote.present !== null) corrections.push({ allergen: vote.allergen, present: vote.present });
  const view = new Set<string>(dto.allergens);
  for (const c of corrections) {
    if (c.present) view.add(c.allergen);
    else view.delete(c.allergen);
  }
  return {
    ...dto,
    allergenCorrections: ALLERGENS.flatMap((a) => corrections.filter((c) => c.allergen === a)),
    householdAllergens: ALLERGENS.filter((a) => view.has(a)),
  };
}
